import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Chart, CategoryScale, LinearScale, Title, BarElement, Tooltip } from "chart.js";
import { Card } from "react-bootstrap";
import { fetchReportInteractions } from "../../../services/httpClient";

// Register the required Chart.js scales
Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

interface InteractionsBarChartProps {
  data?: any[];
  startDate?: string;
  endDate?: string;
  useDatePicker?: boolean;
}

export const InteractionsBarChart = ({ data, startDate, endDate, useDatePicker = false }: InteractionsBarChartProps) => {
  const [fetchedData, setFetchedData] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (data) {
        setFetchedData(data);
      } else {
        const starting = new Date();
        starting.setDate(starting.getDate() - 365);
        let start = starting.toISOString().slice(0, 10);
        let end = new Date().toISOString().slice(0, 10);

        if (useDatePicker) {
          start = startDate ?? start;
          end = endDate ?? end;
        }

        await getInteractions(start!, end!);
      }
    };

    fetchData();
  }, [data, startDate, endDate]);

  const getInteractions = async (start: string, end: string) => {
    fetchReportInteractions(start, end)
      .then(async (res: any) => {
        const data = res[0].data;

        const creators = data.creator_counts || [];
        setFetchedData(
          creators.map((c: any) => ({
            created_by: c.created_by,
            creator_firstname: c.firstname,
            creator_lastname: c.lastname,
            count: parseInt(c.count),
          }))
        );
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const chartData = {
    labels: fetchedData.map((item: any) => item.creator_firstname + " " + item.creator_lastname),
    datasets: [
      {
        label: "Interactions",
        data: fetchedData.map((item: any) => item.count),
        backgroundColor: ["#5D82A0"],
      },
    ],
  };

  const maxCount = fetchedData.length ? Math.max(...fetchedData.map((item: any) => item.count)) : 0;
  const maxCountWithBuffer = Math.ceil(maxCount / 5) * 5;

  const options = {
    indexAxis: "y" as const,
    maintainAspectRatio: false,
    datasets: {
      bar: {
        maxBarThickness: 30,
      },
    },
    scales: {
      x: {
        max: maxCountWithBuffer,
      },
      y: {
        beginAtZero: true,
        max: maxCount + 50,
        ticks: {
          font: {
            size: 12,
          },
        },
      },
    },
    plugins: {
      title: {
        display: true,
        text: "Case Manager Interactions",
      },
    },
    elements: {
      bar: {
        borderWidth: 1,
        borderRadius: 4,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
      },
    },
  };

  return (
    <Card className="p-3">
      <div style={{ height: `${chartData.labels.length * 50}px`, minHeight: "150px" }}>
        <Bar data={chartData} options={options} />
      </div>
    </Card>
  );
};
